import { ChangeDetectorRef, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material';
import { MaintenancesApi } from '@api/maintenances/maintenances.api';
import { Maintenance, MaintenanceProductServicePageResult } from '@core/interfaces';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { isNil } from 'lodash';
import { debounceTime, first } from 'rxjs/operators';
import { sentry } from 'sentry';
import { AuthenticationService } from '../../../../../../core/authentication/authentication.service';
import english from '../../../../i18n/en.json';
import spanish from '../../../../i18n/es.json';
import portuguese from '../../../../i18n/pt.json';
import { MaintenanceDialogData } from '../../dialog-maintenances.component';

@Component({
  selector: 'tab-products',
  styleUrls: ['tab-products.component.scss'],
  templateUrl: 'tab-products.component.html',
  encapsulation: ViewEncapsulation.None
})
export class TabProductsComponent implements OnInit {
  public paginationLimit: number;
  public paginationOffset: number;
  public loadingProducts: boolean;
  public searchInputValue: FormControl;
  public maintenance: Maintenance;
  public products: MaintenanceProductServicePageResult;
  public filteredProducts: MaintenanceProductServicePageResult;
  public orderBy = 'name_asc';

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: MaintenanceDialogData,
    private readonly progressBar: FuseProgressBarService,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly maintenancesApiService: MaintenancesApi,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly authenticationService: AuthenticationService
  ) {
    this.products = null;
    this.filteredProducts = null;
    this.paginationLimit = 0;
    this.paginationOffset = 0;
    this.loadingProducts = true;
    this.searchInputValue = new FormControl();
    this.maintenance = this.data.maintenance;
    this.fuseTranslationLoaderService.loadJsonTranslations({english, portuguese, spanish});
  }

  public async ngOnInit(): Promise<void> {
    await this.loadProducts();

    this.searchInputValue
      .valueChanges
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.paginationOffset = 0;
        this.loadProducts();
      });
  }

  public async toNextPage(): Promise<void> {
    this.paginationOffset += 5;
    await this.loadProducts();
  }

  public async toPreviousPage(): Promise<void> {
    this.paginationOffset -= 5;
    await this.loadProducts();
  }

  public async orderByName(): Promise<void> {
    if (this.orderBy === 'name_asc') {
      this.orderBy = 'name_desc';
    } else {
      this.orderBy = 'name_asc';
    }

    await this.loadProducts();
  }

  private async showLoading(): Promise<void> {
    this.loadingProducts = true;
    const isProgressBarVisible = await this.progressBar.visible
      .pipe(first())
      .toPromise();

    if (!isProgressBarVisible) {
      this.progressBar.show();
      this.changeDetectorRef.markForCheck();
    }
  }

  private async hideLoading(): Promise<void> {
    this.loadingProducts = false;
    const isProgressBarVisible = await this.progressBar.visible
      .pipe(first())
      .toPromise();

    if (isProgressBarVisible) {
      this.progressBar.hide();
      this.changeDetectorRef.markForCheck();
    }
  }

  private async loadProducts(): Promise<void> {
    await this.showLoading();

    if (isNil(this.data.maintenance.company && this.data.maintenance.company.id)) {
      await this.hideLoading();
      return;
    }

    const variables: Record<string, any> = {
      limit: 5,
      offset: this.paginationOffset,
      maintenanceId: this.data.maintenance.id,
      orderBy: this.orderBy,
      where: {
        type: 'product'
      }
    };

    try {
      this.products = await this.maintenancesApiService
        .searchMaintenanceProductsServices(variables)
        .toPromise();

      if (this.products.totalCount === null) {
        sentry.captureMessage('Error loading products', {
          extra: {
            variables: JSON.stringify(variables)
          },
          user: {
            id: this.authenticationService.user.id,
            email: this.authenticationService.user.email,
            username: this.authenticationService.user.name,
          }
        });
      }

      const hasInputValue = Boolean(this.searchInputValue.value);
      if (hasInputValue) {
        variables.where = {
          name_contains: this.searchInputValue.value
        };
      }
      this.filteredProducts = hasInputValue
        ? await this.maintenancesApiService.searchMaintenanceProductsServices(variables).toPromise()
        : this.products;


      this.paginationLimit = this.filteredProducts && this.filteredProducts.totalCount <= 5
        ? this.filteredProducts.totalCount
        : 5;
    } catch (err) {
      sentry.captureException(err, {
        extra: {
          variables: JSON.stringify(variables)
        },
        user: {
          id: this.authenticationService.user.id,
          email: this.authenticationService.user.email,
          username: this.authenticationService.user.name,
        }
      });
    } finally {
      this.hideLoading();
    }
  }
}
